import React, { useState, useMemo, useEffect } from "react";
import { Routes, Route, Navigate, Link, useNavigate, useLocation } from "react-router-dom";
import SalonPublicThemeRoute from "../common/SalonPublicThemeRoute";
import ClientDashboard from "./salonList/ClientDashboard";
import ClientSalon from "./salonDetails/ClientSalon";
import ClientHeader from "./header/ClientHeader";
import AppointmentRebooking from "./salonDetails/AppointmentRebooking";
import UpdateAppointment from "./appointment/UpdateAppointment";
import FavoritesList from "./FavoritesList";
import { Box, Container, Typography, Paper, Grid, Divider, Chip, Button, CircularProgress, TextField, IconButton } from "@mui/material";
import { useAppointmentsRetrieveQuery, useAppointmentsListQuery, useAppointmentsUpcomingRetrieveQuery, useAuthProfileRetrieveQuery, useAuthProfileUpdateMutation, useAppointmentsPartialUpdateMutation, useAppointmentsDestroyMutation } from "../../services/GLOWClient";
import MuiDialog from "../../SystemDesign/MuiDialog";
import { useAuth } from "../../context/AuthContext";
import { theme } from "../../theme";
import ClientProfile from "./profile/ClientProfile";
import ClientMessagesPage from './messaging/ClientMessagesPage';
import { useTranslation } from 'react-i18next';
// Enhanced Client Profile Component
import {
  Edit as EditIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Save as SaveIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarTodayIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
  AccessTime as AccessTimeIcon,
  AttachMoney as MoneyIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { Alert } from '@mui/material';
import AppointmentProposalCard from './appointment/AppointmentProposalCard';
import {
  useAcceptProposalMutation,
  useCancelProposalMutation,
} from '../../services/appointmentWorkflowApi';

// Client Appointment Detail Component
const AppointmentDetail = ({ appointmentId }) => {
  const { t } = useTranslation();
  const { data: appointment, isLoading, error, refetch } = useAppointmentsRetrieveQuery({ appointmentId });
  const [proposalError, setProposalError] = useState('');
  const [acceptProposal, { isLoading: isAcceptingProposal }] = useAcceptProposalMutation();
  const [cancelProposal, { isLoading: isCancellingProposal }] = useCancelProposalMutation();

  const respondToProposal = async (action, proposalId) => {
    setProposalError('');
    try {
      if (action === 'accept') {
        await acceptProposal(proposalId).unwrap();
      } else {
        await cancelProposal({
          id: proposalId,
          reason: t('appointment_workflow.proposal.client_cancel_reason'),
        }).unwrap();
      }
      await refetch();
    } catch (requestError) {
      setProposalError(requestError?.data?.detail || t('appointment_workflow.errors.stale_state'));
      await refetch();
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{t('client.loading_appointment_details')}</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error">{t('client.error_loading_appointment')}</Typography>
      </Box>
    );
  }

  if (!appointment) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography>{t('client.appointment_not_found')}</Typography>
      </Box>
    );
  }

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('locale'), { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const canCancelAppointment = (appointment) => {
    if (!appointment?.start_time) return false;
    const startMs = new Date(appointment.start_time).getTime();
    const nowMs = Date.now();
    const diffMs = startMs - nowMs;
    return diffMs > 24 * 60 * 60 * 1000;
  };

  // Get status color and label
  const getStatusInfo = (status) => {
    switch(status) {
      case 'confirmed':
        return { color: 'success', label: t('client.status.confirmed') };
      case 'pending':
        return { color: 'warning', label: t('appointment_workflow.status.pending_salon') };
      case 'proposal_pending':
        return { color: 'warning', label: t('appointment_workflow.status.pending_client') };
      case 'proposal_expired':
        return { color: 'error', label: t('appointment_workflow.status.expired') };
      case 'cancelled':
        return { color: 'error', label: t('client.status.cancelled') };
      case 'completed':
        return { color: 'info', label: t('client.status.completed') };
      case 'reschedule_required':
        return { color: 'warning', label: t('client.status.reschedule_required'), customColor: '#ff9800' };
      default:
        return { color: 'default', label: status };
    }
  };

  const statusInfo = getStatusInfo(appointment.status);

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        {/* Header with Gradient */}
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.secondary[500]} 100%)`,
          p: 4,
          color: 'white',
          position: 'relative'
        }}>
          {/* Back Button */}
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => window.history.back()}
            sx={{ 
              position: 'absolute',
              top: { xs: 12, sm: 24 },
              left: { xs: 12, sm: 24 },
              color: 'white',
              fontWeight: 600,
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              px: { xs: 2, sm: 3 },
              py: { xs: 0.75, sm: 1 },
              borderRadius: 2,
              fontSize: { xs: '0.875rem', sm: '1rem' },
              minWidth: 'auto',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                transform: 'translateX(-4px)',
                transition: 'all 0.2s'
              }
            }}
          >
            {t('client.back')}
          </Button>

          {/* Title */}
          <Typography variant="h4" sx={{ 
            fontWeight: 700, 
            mb: 3, 
            textShadow: '0 2px 4px rgba(0,0,0,0.2)',
            textAlign: 'center',
            pt: { xs: 4, sm: 0 }
          }}>
            {t('client.appointment_details_title')}
          </Typography>
          
          {/* Salon Info */}
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              {appointment.salon_name}
            </Typography>
            {appointment.salon_address && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.95 }}>
                <LocationOnIcon sx={{ fontSize: '1.1rem' }} />
                <Typography variant="body2">
                  {appointment.salon_address}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        {/* Service Details Section */}
        <Box sx={{ p: 3 }}>
          {appointment.status === 'proposal_pending' && appointment.active_proposal && (
            <Box sx={{ mb: 3 }}>
              <AppointmentProposalCard
                appointment={appointment}
                proposal={appointment.active_proposal}
                busy={isAcceptingProposal || isCancellingProposal}
                error={proposalError}
                onAccept={(proposalId) => respondToProposal('accept', proposalId)}
                onCancel={(proposalId) => respondToProposal('cancel', proposalId)}
                labels={{
                  title: t('appointment_workflow.proposal.title'),
                  original: t('appointment_workflow.proposal.original'),
                  proposed: t('appointment_workflow.proposal.proposed'),
                  service: t('appointment_workflow.proposal.service'),
                  employee: t('appointment_workflow.proposal.employee'),
                  dateTime: t('appointment_workflow.proposal.date_time'),
                  duration: t('appointment_workflow.proposal.duration'),
                  price: t('appointment_workflow.proposal.price'),
                  deadline: t('appointment_workflow.proposal.deadline'),
                  remaining: t('appointment_workflow.proposal.remaining'),
                  accept: t('appointment_workflow.proposal.accept'),
                  cancel: t('appointment_workflow.proposal.cancel'),
                }}
              />
            </Box>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: theme.colors.primary[700] }}>
              {t('client.appointment_info')}
            </Typography>
            <Chip 
              label={statusInfo.label} 
              color={statusInfo.color}
              size="small"
              sx={{ 
                fontSize: '0.75rem',
                fontWeight: 600,
                height: 28,
                ...(statusInfo.customColor && {
                  backgroundColor: statusInfo.customColor,
                  color: 'white'
                })
              }}
            />
          </Box>
          
          <Box sx={{ 
            p: 3,
            backgroundColor: theme.colors.primary[50],
            borderRadius: 2,
            border: `1px solid ${theme.colors.primary[200]}`,
            mb: 3
          }}>
            {/* Date, Heure, Durée - Centrés */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 6, mb: 3, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon sx={{ fontSize: '1.2rem', color: theme.colors.primary[600] }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem' }}>
                    {t('client.date')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                    {new Date(appointment.start_time).toLocaleDateString('fr-FR', { 
                      day: 'numeric', 
                      month: 'long',
                      year: 'numeric'
                    })}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: '1.2rem', color: theme.colors.primary[600] }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem' }}>
                    {t('client.time')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                    {new Date(appointment.start_time).toLocaleTimeString('fr-FR', { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <AccessTimeIcon sx={{ fontSize: '1.2rem', color: theme.colors.primary[600] }} />
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.7rem' }}>
                    {t('client.duration')}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                    {appointment.service_formatted_duration || `${Math.round((new Date(appointment.end_time) - new Date(appointment.start_time)) / 60000)} ${t('client.minutes')}`}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {appointment.status === 'cancelled' && appointment.cancellation_reason && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#fef2f2',
                  borderRadius: 2,
                  border: '1px solid #fecaca',
                }}
              >
                <Typography variant="body2" sx={{ color: '#b91c1c', fontWeight: 700, mb: 0.5 }}>
                  {t('client.cancellation_reason')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#7f1d1d' }}>
                  {appointment.cancellation_reason}
                </Typography>
              </Box>
            )}

            <Divider sx={{ mb: 3 }} />

            {/* Services Table */}
            <Box sx={{ 
              backgroundColor: 'white',
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              {/* Table Header */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 2,
                p: 2,
                backgroundColor: theme.colors.primary[100],
                fontWeight: 600
              }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.primary[800] }}>
                  {t('client.service')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.primary[800], textAlign: 'center' }}>
                  {t('client.duration')}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: theme.colors.primary[800], textAlign: 'right' }}>
                  {t('client.price')}
                </Typography>
              </Box>

              {/* Table Row */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: '2fr 1fr 1fr',
                gap: 2,
                p: 2,
                borderBottom: appointment.team_member_name ? `1px solid ${theme.colors.primary[100]}` : 'none'
              }}>
                <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: 500 }}>
                  {appointment.service_name}
                </Typography>
                <Typography variant="body2" sx={{ color: '#475569', textAlign: 'center' }}>
                  {appointment.service_formatted_duration || `${Math.round((new Date(appointment.end_time) - new Date(appointment.start_time)) / 60000)} ${t('client.minutes')}`}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.colors.secondary[600], fontWeight: 700, textAlign: 'right' }}>
                  {appointment.service_price} DA
                </Typography>
              </Box>

              {/* Team Member */}
              {appointment.team_member_name && (
                <Box sx={{ p: 2, backgroundColor: theme.colors.primary[25] }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: '1rem', color: theme.colors.primary[600] }} />
                    <Typography variant="body2" sx={{ color: '#475569', fontWeight: 500 }}>
                      {t('client.with_member', { name: appointment.team_member_name })}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

        {(appointment.status === 'pending' || appointment.status === 'confirmed' || appointment.status === 'reschedule_required') && (
          <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #e2e8f0', display: 'flex', gap: 1.5 }}>
            <Button
              variant="contained"
              onClick={() => window.location.href = `/client/salon/${appointment.salon}/update?updateAppointment=${appointment.appointment_id}`}
               sx={{
                backgroundColor: theme.colors.primary[600],
                color: 'white',
                borderRadius: 2,
                py: 1.2,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                flex: 1,
                '&:hover': {
                  backgroundColor: theme.colors.primary[700]
                }
              }}
            >
              📅 {t('client.reschedule')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {}}
              disabled={!canCancelAppointment(appointment) && appointment.status !== 'reschedule_required'}
              sx={{
                borderColor: '#dc2626',
                color: '#dc2626',
                borderRadius: 2,
                py: 1.2,
                fontSize: '0.875rem',
                fontWeight: 600,
                textTransform: 'none',
                minWidth: '100px',
                '&.Mui-disabled': {
                  opacity: 0.6,
                  borderColor: '#fecaca',
                  color: '#fca5a5',
                },
                '&:hover': {
                  backgroundColor: '#fef2f2',
                  borderColor: '#b91c1c'
                }
              }}
            >
              ❌ {t('client.cancel')}
            </Button>
          </Box>
        )}
      </Box>
    </Paper>
  </Container>
);
};

// Client Appointments List Component
// REMPLACER LA FONCTION AppointmentsList (ligne 338 jusqu'avant "export default ClientView")
// PAR CE CODE :

const AppointmentsList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [statusFilter, setStatusFilter] = useState('all');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [highlightedAppointmentId, setHighlightedAppointmentId] = useState(null);
  const { data: upcomingAppointments, isLoading: upcomingLoading, refetch: refetchUpcoming } = useAppointmentsUpcomingRetrieveQuery();
  const { data: allAppointments, isLoading: allLoading, refetch: refetchAll } = useAppointmentsListQuery({
    client: undefined,
    ordering: '-start_time',
  });

  const isLoading = upcomingLoading || allLoading;

  // Recharger les données au montage du composant
  useEffect(() => {
    refetchUpcoming();
    refetchAll();
  }, []);

  // Gérer la mise en évidence du RDV depuis la navigation
  useEffect(() => {
    if (location.state?.highlightAppointmentId) {
      setHighlightedAppointmentId(location.state.highlightAppointmentId);
      
      // Forcer le rechargement des données
      const reloadData = async () => {
        await Promise.all([refetchUpcoming(), refetchAll()]);
        
        // Scroll vers le RDV après le chargement des données
        if (location.state?.scrollToAppointment) {
          setTimeout(() => {
            const element = document.getElementById(`appointment-${location.state.highlightAppointmentId}`);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 300);
        }
      };
      
      reloadData();
      
      // Retirer la mise en évidence après 5 secondes
      setTimeout(() => {
        setHighlightedAppointmentId(null);
      }, 5000);
    }
  }, [location.state, refetchUpcoming, refetchAll]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(t('locale'), { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric',
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const getStatusInfo = (status) => {
    switch(status) {
      case 'confirmed':
        return { color: 'success', label: t('client.status.confirmed') };
      case 'pending':
        return { color: 'warning', label: t('appointment_workflow.status.pending_salon') };
      case 'proposal_pending':
        return { color: 'warning', label: t('appointment_workflow.status.pending_client') };
      case 'proposal_expired':
        return { color: 'error', label: t('appointment_workflow.status.expired') };
      case 'cancelled':
        return { color: 'error', label: t('client.status.cancelled') };
      case 'completed':
        return { color: 'info', label: t('client.status.completed') };
      case 'reschedule_required':
        return { color: 'warning', label: t('client.status.reschedule_required'), customColor: '#ff9800' };
      default:
        return { color: 'default', label: status };
    }
  };

  const canCancelAppointment = (appointment) => {
    if (!appointment?.start_time) return false;
    const startMs = new Date(appointment.start_time).getTime();
    const nowMs = Date.now();
    const diffMs = startMs - nowMs;
    return diffMs > 24 * 60 * 60 * 1000;
  };

  const historicalAppointments = useMemo(() => {
    if (!allAppointments) return [];
    const now = new Date();
    return allAppointments.filter(appointment => {
      const appointmentDate = new Date(appointment.start_time);
      return appointmentDate < now;
    });
  }, [allAppointments]);

  const filteredUpcomingAppointments = upcomingAppointments?.filter(appointment => {
    const appointmentDate = new Date(appointment.start_time);
    const isSameMonth = appointmentDate.getMonth() === selectedDate.getMonth() && 
                        appointmentDate.getFullYear() === selectedDate.getFullYear();
    
    if (statusFilter === 'all') return isSameMonth;
    return appointment.status === statusFilter && isSameMonth;
  }) || [];

  const filteredHistoricalAppointments = historicalAppointments?.filter(appointment => {
    const appointmentDate = new Date(appointment.start_time);
    const isSameMonth = appointmentDate.getMonth() === selectedDate.getMonth() && 
                        appointmentDate.getFullYear() === selectedDate.getFullYear();
    
    if (statusFilter === 'all') return isSameMonth;
    return appointment.status === statusFilter && isSameMonth;
  }) || [];

  // Fonction pour regrouper les RDV par salon et par date
  const groupAppointmentsBySalonAndDate = (appointments) => {
    const grouped = {};
    
    appointments.forEach(appointment => {
      const date = new Date(appointment.start_time).toLocaleDateString(t('locale'));
      const salonId = appointment.salon;
      const key = `${salonId}_${date}`;
      
      if (!grouped[key]) {
        grouped[key] = {
          salon: appointment.salon,
          salonName: appointment.salon_name,
          salonAddress: appointment.salon_address,
          salonPhoto: appointment.salon_photo,
          date: date,
          appointments: []
        };
      }
      
      grouped[key].appointments.push(appointment);
    });
    
    Object.values(grouped).forEach(group => {
      group.appointments.sort((a, b) => 
        new Date(a.start_time) - new Date(b.start_time)
      );
    });
    
    return Object.values(grouped);
  };

  const groupedUpcomingAppointments = useMemo(() => 
    groupAppointmentsBySalonAndDate(filteredUpcomingAppointments),
    [filteredUpcomingAppointments]
  );

  const groupedHistoricalAppointments = useMemo(() => 
    groupAppointmentsBySalonAndDate(filteredHistoricalAppointments),
    [filteredHistoricalAppointments]
  );

  const [updateAppointment, { isLoading: isCancelling }] = useAppointmentsPartialUpdateMutation();
  const [deleteAppointment, { isLoading: isDeleting }] = useAppointmentsDestroyMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  
  const handleCancelAppointment = async () => {
    if (!appointmentToCancel) return;

    if (!canCancelAppointment(appointmentToCancel)) {
      alert(t('client.cannot_cancel_24h'));
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      return;
    }
    
    try {
      await updateAppointment({
        appointmentId: appointmentToCancel.appointment_id,
        patchedAppointmentUpdateRequest: {
          status: 'cancelled'
        }
      }).unwrap();
      
      setCancelDialogOpen(false);
      setAppointmentToCancel(null);
      await Promise.all([refetchUpcoming(), refetchAll()]);
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      alert(t('client.error_cancelling'));
    }
  };

  const handleDeleteAppointment = async () => {
    if (!appointmentToDelete) return;
    
    try {
      await deleteAppointment({
        appointmentId: appointmentToDelete.appointment_id
      }).unwrap();
      
      setDeleteDialogOpen(false);
      setAppointmentToDelete(null);
      await Promise.all([refetchUpcoming(), refetchAll()]);
    } catch (error) {
      console.error('Error deleting appointment:', error);
      alert(t('client.error_deleting'));
    }
  };
  
  const openCancelDialog = (appointment) => {
    if (!canCancelAppointment(appointment)) {
      alert(t('client.cannot_cancel_24h'));
      return;
    }
    setAppointmentToCancel(appointment);
    setCancelDialogOpen(true);
  };
  
  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setAppointmentToCancel(null);
  };

  const openDeleteDialog = (appointment) => {
    setAppointmentToDelete(appointment);
    setDeleteDialogOpen(true);
  };
  
  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setAppointmentToDelete(null);
  };

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h5" component="h1" sx={{ mb: 3 }}>
          {t('client.my_appointments')}
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant= "h4" sx={{ mb: 3, fontWeight:  { xs: '200', sm: '400', md: '700' }, color: theme.colors.primary[700] }}>
        {t('client.my_appointments')}
      </Typography>

      {/* Status Filter */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
        <Button 
          variant={statusFilter === 'all' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('all')}
          size="small"
        >
          {t('client.filters.all')}
        </Button>
        <Button 
          variant={statusFilter === 'confirmed' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('confirmed')}
          color="success"
          size="small"
        >
          {t('client.filters.confirmed')}
        </Button>
        <Button 
          variant={statusFilter === 'pending' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('pending')}
          color="warning"
          size="small"
        >
          {t('client.filters.pending')}
        </Button>
        <Button 
          variant={statusFilter === 'completed' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('completed')}
          color="info"
          size="small"
        >
          {t('client.filters.completed')}
        </Button>
        <Button 
          variant={statusFilter === 'cancelled' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('cancelled')}
          color="error"
          size="small"
        >
          {t('client.filters.cancelled')}
        </Button>
        <Button 
          variant={statusFilter === 'reschedule_required' ? 'contained' : 'outlined'}
          onClick={() => setStatusFilter('reschedule_required')}
          size="small"
          sx={{
            backgroundColor: statusFilter === 'reschedule_required' ? '#ff9800' : undefined,
            borderColor: '#ff9800',
            color: statusFilter === 'reschedule_required' ? 'white' : '#ff9800',
            '&:hover': {
              backgroundColor: statusFilter === 'reschedule_required' ? '#f57c00' : '#fff3e0',
            }
          }}
        >
          {t('client.filters.reschedule_required')}
        </Button>
      </Box>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Upcoming Appointments Section */}
          {groupedUpcomingAppointments.length > 0 && (
            <>
              <Typography variant="h6" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                {t('client.upcoming_appointments')}
              </Typography>
              <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {groupedUpcomingAppointments.map((group) => (
                  <Paper 
                    key={`${group.salon}_${group.date}`}
                    elevation={0}
                    sx={{ 
                      border: '1px solid',
                      borderColor: '#e2e8f0',
                      borderRadius: 3,
                      overflow: 'hidden',
                      backgroundColor: 'white'
                    }}
                  >
                    {/* En-tête du groupe : Salon + Date */}
                    <Box sx={{ 
                      p: 2.5, 
                      backgroundColor: theme.colors.primary[50],
                      borderBottom: `2px solid ${theme.colors.primary[200]}`
                    }}>
                      <Typography 
                        variant="h6" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/salon/${group.salon}`);
                        }}
                        sx={{ 
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: theme.colors.primary[700],
                          mb: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}>
                        {group.salonName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                          {group.salonAddress}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 1 }}>
                        📅 {group.date} • {t('client.appointments_count', { count: group.appointments.length })}
                      </Typography>
                    </Box>

                    {/* Liste des RDV du groupe */}
                    <Box sx={{ p: 2 }}>
                      {group.appointments.map((appointment, index) => {
                        const statusInfo = getStatusInfo(appointment.status);
                        return (
                          <Box 
                            key={appointment.appointment_id}
                            id={`appointment-${appointment.appointment_id}`}
                            onClick={() => navigate(`/client/appointments/${appointment.appointment_id}`)}
                            sx={{
                              p: 2.5,
                              mb: index < group.appointments.length - 1 ? 2 : 0,
                              backgroundColor: highlightedAppointmentId === appointment.appointment_id 
                                ? theme.colors.primary[100] 
                                : theme.colors.primary[25],
                              borderRadius: 2,
                              border: highlightedAppointmentId === appointment.appointment_id
                                ? `2px solid ${theme.colors.primary[500]}`
                                : `1px solid ${theme.colors.primary[100]}`,
                              transition: 'all 0.3s ease',
                              boxShadow: highlightedAppointmentId === appointment.appointment_id
                                ? '0 4px 12px rgba(0,0,0,0.15)'
                                : 'none',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme.colors.primary[50],
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            {/* Nom prestation + Status */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: '1.1rem',
                                  fontWeight: 700,
                                  color: theme.colors.primary[700]
                                }}>
                                {appointment.service_name}
                              </Typography>
                              <Chip 
                                label={statusInfo.label} 
                                color={statusInfo.color} 
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  height: 24
                                }}
                              />
                            </Box>

                            {/* Détails du RDV */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem',
                                color: '#334155',
                                fontWeight: 600
                              }}>
                                🕐 {new Date(appointment.start_time).toLocaleTimeString('fr-FR', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </Typography>
                              
                              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>•</Typography>

                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem',
                                color: '#475569'
                              }}>
                                ⏱️ {appointment.service_formatted_duration}
                              </Typography>

                              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>•</Typography>

                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem',
                                color: theme.colors.secondary[600],
                                fontWeight: 700
                              }}>
                                💰 {appointment.service_price} DA
                              </Typography>

                              {appointment.team_member_name && (
                                <>
                                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>•</Typography>
                                  <Typography variant="body2" sx={{ 
                                    fontSize: '0.875rem',
                                    color: '#475569'
                                  }}>
                                    👤 {appointment.team_member_name}
                                  </Typography>
                                </>
                              )}
                            </Box>

                            {/* Boutons d'action */}
                            {(appointment.status === 'pending' || appointment.status === 'confirmed' || appointment.status === 'reschedule_required') && (
                              <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                                <Button
                                  variant="contained"
                                  fullWidth
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.location.href = `/client/salon/${appointment.salon}/update?updateAppointment=${appointment.appointment_id}`;
                                  }}
                                  sx={{
                                    backgroundColor: theme.colors.primary[600],
                                    color: 'white',
                                    borderRadius: 2,
                                    py: 1.2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                      backgroundColor: theme.colors.primary[700]
                                    }
                                  }}
                                >
                                  {appointment.status === 'reschedule_required' ? `📅 ${t('client.reschedule')}` : `✏️ ${t('client.edit')}`}
                                </Button>
                                <Button
                                  variant="outlined"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCancelDialog(appointment);
                                  }}
                                  disabled={!canCancelAppointment(appointment) && appointment.status !== 'reschedule_required'}
                                  sx={{
                                    borderColor: '#dc2626',
                                    color: '#dc2626',
                                    borderRadius: 2,
                                    py: 1.2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    minWidth: '100px',
                                    '&.Mui-disabled': {
                                      opacity: 0.6,
                                      borderColor: '#fecaca',
                                      color: '#fca5a5',
                                    },
                                    '&:hover': {
                                      backgroundColor: '#fef2f2',
                                      borderColor: '#b91c1c'
                                    }
                                  }}
                                >
                                  ❌ {t('client.cancel')}
                                </Button>
                              </Box>
                            )}
                            {appointment.status === 'cancelled' && (
                              <Box sx={{ display: 'flex', gap: 1.5 }}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openDeleteDialog(appointment);
                                  }}
                                  sx={{
                                    borderColor: '#dc2626',
                                    color: '#dc2626',
                                    borderRadius: 2,
                                    py: 1.2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    textTransform: 'none',
                                    '&:hover': {
                                      backgroundColor: '#fef2f2',
                                      borderColor: '#b91c1c'
                                    }
                                  }}
                                >
                                  🗑️ {t('client.delete')}
                                </Button>
                              </Box>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {/* Divider */}
          {groupedUpcomingAppointments.length > 0 && groupedHistoricalAppointments.length > 0 && (
            <Divider sx={{ my: 4 }}>
              <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
                {t('client.history')}
              </Typography>
            </Divider>
          )}

          {/* Historical Appointments Section */}
          {groupedHistoricalAppointments.length > 0 && (
            <>
              {groupedUpcomingAppointments.length === 0 && (
                <Typography variant="h6" sx={{ mb: 2, color: '#0f172a', fontWeight: 600 }}>
                  {t('client.past_appointments')}
                </Typography>
              )}
              <Box sx={{ mb: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {groupedHistoricalAppointments.map((group) => (
                  <Paper 
                    key={`${group.salon}_${group.date}_past`}
                    elevation={0}
                    sx={{ 
                      border: '1px solid',
                      borderColor: '#e2e8f0',
                      borderRadius: 3,
                      overflow: 'hidden',
                      backgroundColor: 'white'
                    }}
                  >
                    {/* En-tête du groupe */}
                    <Box sx={{ 
                      p: 2.5, 
                      backgroundColor: theme.colors.secondary[50],
                      borderBottom: `2px solid ${theme.colors.secondary[200]}`
                    }}>
                      <Typography 
                        variant="h6" 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/salon/${group.salon}`);
                        }}
                        sx={{ 
                          fontSize: '1.25rem',
                          fontWeight: 700,
                          color: theme.colors.secondary[700],
                          mb: 0.5,
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline'
                          }
                        }}>
                        {group.salonName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <LocationOnIcon sx={{ fontSize: '1rem', color: '#64748b' }} />
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                          {group.salonAddress}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ color: theme.colors.text.secondary, mt: 1 }}>
                        📅 {group.date} • {t('client.appointments_count', { count: group.appointments.length })}
                      </Typography>
                    </Box>

                    {/* Liste des RDV */}
                    <Box sx={{ p: 2 }}>
                      {group.appointments.map((appointment, index) => {
                        const statusInfo = getStatusInfo(appointment.status);
                        return (
                          <Box 
                            key={appointment.appointment_id}
                            id={`appointment-${appointment.appointment_id}`}
                            onClick={() => navigate(`/client/appointments/${appointment.appointment_id}`)}
                            sx={{
                              p: 2.5,
                              mb: index < group.appointments.length - 1 ? 2 : 0,
                              backgroundColor: highlightedAppointmentId === appointment.appointment_id 
                                ? theme.colors.secondary[100] 
                                : theme.colors.secondary[25],
                              borderRadius: 2,
                              border: highlightedAppointmentId === appointment.appointment_id
                                ? `2px solid ${theme.colors.secondary[500]}`
                                : `1px solid ${theme.colors.secondary[100]}`,
                              transition: 'all 0.3s ease',
                              boxShadow: highlightedAppointmentId === appointment.appointment_id
                                ? '0 4px 12px rgba(0,0,0,0.15)'
                                : 'none',
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: theme.colors.secondary[50],
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            {/* Nom prestation + Status */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 1.5 }}>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  fontSize: '1.1rem',
                                  fontWeight: 700,
                                  color: theme.colors.secondary[700]
                                }}>
                                {appointment.service_name}
                              </Typography>
                              <Chip 
                                label={statusInfo.label} 
                                color={statusInfo.color} 
                                size="small"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  height: 24
                                }}
                              />
                            </Box>

                            {/* Détails du RDV */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem',
                                color: '#334155',
                                fontWeight: 600
                              }}>
                                🕐 {new Date(appointment.start_time).toLocaleTimeString('fr-FR', { 
                                  hour: '2-digit', 
                                  minute: '2-digit' 
                                })}
                              </Typography>
                              
                              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>•</Typography>

                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem',
                                color: '#475569'
                              }}>
                                ⏱️ {appointment.service_formatted_duration}
                              </Typography>

                              <Typography variant="body2" sx={{ color: '#cbd5e1' }}>•</Typography>

                              <Typography variant="body2" sx={{ 
                                fontSize: '0.875rem',
                                color: theme.colors.secondary[600],
                                fontWeight: 700
                              }}>
                                💰 {appointment.service_price} DA
                              </Typography>

                              {appointment.team_member_name && (
                                <>
                                  <Typography variant="body2" sx={{ color: '#cbd5e1' }}>•</Typography>
                                  <Typography variant="body2" sx={{ 
                                    fontSize: '0.875rem',
                                    color: '#475569'
                                  }}>
                                    👤 {appointment.team_member_name}
                                  </Typography>
                                </>
                              )}
                            </Box>

                            {/* Boutons d'action pour RDV passés */}
                            <Box sx={{ display: 'flex', gap: 1.5, flexDirection: { xs: 'column', sm: 'row' } }}>
                              <Button
                                variant="contained"
                                fullWidth
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.location.href = `/client/salon/${appointment.salon}/rebook?rebookAppointment=${appointment.appointment_id}`;
                                }}
                                sx={{
                                  backgroundColor: theme.colors.secondary[600],
                                  color: 'white',
                                  borderRadius: 2,
                                  py: 1.2,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  '&:hover': {
                                    backgroundColor: theme.colors.secondary[700]
                                  }
                                }}
                              >
                                🔄 {t('client.rebook')}
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDeleteDialog(appointment);
                                }}
                                sx={{
                                  borderColor: '#dc2626',
                                  color: '#dc2626',
                                  borderRadius: 2,
                                  py: 1.2,
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  textTransform: 'none',
                                  minWidth: '100px',
                                  '&:hover': {
                                    backgroundColor: '#fef2f2',
                                    borderColor: '#b91c1c'
                                  }
                                }}
                              >
                                🗑️ {t('client.delete')}
                              </Button>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </>
          )}

          {/* No appointments message */}
          {groupedUpcomingAppointments.length === 0 && groupedHistoricalAppointments.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                {t('client.no_appointments_found', { status: statusFilter !== 'all' ? `"${getStatusInfo(statusFilter).label}"` : '' })}
              </Typography>
            </Paper>
          )}
        </>
      )}
      
      {/* Cancel Appointment Dialog */}
      <MuiDialog
        open={cancelDialogOpen}
        onExternalClose={closeCancelDialog}
        title={t('client.cancel_appointment')}
        content={
          <div>
            <p>{t('client.confirm_cancel_message')}</p>
            {appointmentToCancel && (
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <strong>{appointmentToCancel.salon_name}</strong><br/>
                <span>{appointmentToCancel.service_name}</span><br/>
                <span>{formatDate(appointmentToCancel.start_time)}</span>
              </div>
            )}
            <p style={{ marginTop: '16px', color: '#d32f2f', fontSize: '14px' }}>
              {t('client.irreversible_action')}
            </p>
          </div>
        }
        existAction={true}
        actionTitle={t('client.cancel_rdv')}
        actionFunction={handleCancelAppointment}
        isLoading={isCancelling}
        disableAction={appointmentToCancel ? !canCancelAppointment(appointmentToCancel) : false}
        maxWidth="sm"
      />

      {/* Delete Appointment Dialog */}
      <MuiDialog
        open={deleteDialogOpen}
        onExternalClose={closeDeleteDialog}
        title={t('client.delete_appointment')}
        content={
          <div>
            <p>{t('client.confirm_delete_message')}</p>
            {appointmentToDelete && (
              <div style={{ marginTop: '16px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
                <strong>{appointmentToDelete.salon_name}</strong><br/>
                <span>{appointmentToDelete.service_name}</span><br/>
                <span>{formatDate(appointmentToDelete.start_time)}</span>
              </div>
            )}
            <p style={{ marginTop: '16px', color: '#d32f2f', fontSize: '14px', fontWeight: 'bold' }}>
              ⚠️ {t('client.irreversible_delete_warning')}
            </p>
          </div>
        }
        existAction={true}
        actionTitle={t('client.delete_permanently')}
        actionFunction={handleDeleteAppointment}
        isLoading={isDeleting}
        maxWidth="sm"
      />
    </Container>
  );
};

// Enhanced Client Profile Component



// Appointment Detail Route Component
const AppointmentDetailRoute = () => {
  const appointmentId = parseInt(window.location.pathname.split('/').pop(), 10);
  return <AppointmentDetail appointmentId={appointmentId} />;
};

const ClientView = () => {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <ClientHeader />
            <Box sx={{ flexGrow: 1, pt: 3, pb: 4 }}>
                <Container maxWidth="lg">
                    <Routes>
                        {/* Redirect /client to /client/salons */}
                        <Route path="/" element={<Navigate to="/client/salons" replace />} />
                        
                        {/* Salon list route */}
                        <Route path="salons" element={<ClientDashboard />} />
                        
                        {/* Salon details route with salonId parameter */}
                        <Route
                          path="salon/:salonId"
                          element={(
                            <SalonPublicThemeRoute>
                              <ClientSalon />
                            </SalonPublicThemeRoute>
                          )}
                        />
                        
                        {/* Appointment rebooking route */}
                        <Route
                          path="salon/:salonId/rebook"
                          element={(
                            <SalonPublicThemeRoute>
                              <AppointmentRebooking />
                            </SalonPublicThemeRoute>
                          )}
                        />
                        
                        {/* Appointment update route */}
                        <Route path="salon/:salonId/update" element={<UpdateAppointment />} />
                        
                        {/* Appointments routes */}
                        <Route path="appointments" element={<AppointmentsList />} />
                        <Route path="appointments/:appointmentId" element={<AppointmentDetailRoute />} />
                        
                        {/* Favorites route */}
                        <Route path="favorites" element={<FavoritesList />} />
                        
                        {/* Profile routes */}
                        <Route path="profile" element={<ClientProfile />} />
                        <Route path="messages" element={<ClientMessagesPage />} />
                        
                        {/* Default redirect for any other client routes */}
                        <Route path="*" element={<Navigate to="/client/salons" replace />} />
                    </Routes>
                </Container>
            </Box>
        </Box>
    );
};

export default ClientView;
export { AppointmentsList };
